import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import useStyles from '../styles';
import CustomButton from '../../../components/CustomButton';
import { BillingInvoice } from '../types';

interface Props {
  invoice: BillingInvoice;
  formatCurrency: (amount: number) => string;
}

const BillingDFORow: React.FC<Props> = (props) => {
  const { invoice, formatCurrency } = props;
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const formatDate = useMemo(() => (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }, []);

  const getStatusColor = useMemo(() => (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '#4CAF50';
      case 'overdue':
        return '#F44336';
      case 'pending':
        return '#FF9800';
      default:
        return '#6B7280';
    }
  }, []);

  const loadInvoiceDetail = () => {
    if (invoice.publicInvoiceUrl) {
      window.open(invoice.publicInvoiceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <TableRow key={invoice.id} className={classes.tableRow}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{invoice.docNumber}</TableCell>
        <TableCell>{formatDate(invoice.issuedDate)}</TableCell>
        <TableCell>{formatDate(invoice.dueDate)}</TableCell>
        <TableCell>{formatCurrency(invoice.totalAmount)}</TableCell>
        <TableCell>{formatCurrency(invoice.amountDue)}</TableCell>
        <TableCell>
          <span
            className={classes.statusBadge}
            style={{ backgroundColor: getStatusColor(invoice.status) }}
          >
            {invoice.status}
          </span>
        </TableCell>
        <TableCell>
          <CustomButton
            size="small"
            variant="white"
            className={classes.viewButton}
            onClick={loadInvoiceDetail}
            disabled={!invoice.publicInvoiceUrl}
          >
            View
          </CustomButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                Line Items
              </Typography>
              <Table size="small" aria-label="line items">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="center">Unit Price</TableCell>
                    <TableCell align="center">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.salesLineItems && invoice.salesLineItems.map((lineItem) => (
                    <TableRow key={`${invoice.id}-${lineItem.itemName}-${lineItem.description}`}>
                      <TableCell component="th" scope="row">
                        {lineItem.description}
                      </TableCell>
                      <TableCell>{lineItem.itemName}</TableCell>
                      <TableCell align="center">{lineItem.quantity}</TableCell>
                      <TableCell align="center">{formatCurrency(lineItem.unitPrice)}</TableCell>
                      <TableCell align="center">{formatCurrency(lineItem.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default React.memo(BillingDFORow);
